/*
 * Authors: mariushirjanu, Koen
 * Created: 20/04/2015
 * Upgraded: 27/12/2022
 */
import _ from 'underscore';
import { Subscription } from 'rxjs';
import { Component, OnInit, OnDestroy, Input, AfterViewInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { IOrderContext, IOrderContextTag, IOrderInfo } from '../order-entry-data.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { OrderEntryInfoService } from './order-entry-info.service';
import { OrderEntryWebshopService } from '@app/main/order/entry/webshop/order-entry-webshop.service';
import { GaWizardService } from '@app/shared/wizard/ga-wizard.service';
import { OrderEntryPaymentService } from '../payment/order-entry-payment.service';
import { WIZARD_ORDER } from '@app/shared/app-wizards';
import { GaWizard } from '@app/shared/wizard/ga-wizard';
import { IDescribedStringObject, IValueObject } from '@type_definitions/datatypes';
import { POType } from '../../../payment/payment-data.service';
import { GaValidateService, IValidatingComponent } from '@app/shared/formly/ga-validate.service';
import { IOrderType, IStockOrderType, OrderEntryInfoDataService } from './order-entry-info-data.service';
import { EVENT_ORDER_CONTEXT_UPDATED, EVENT_ORDER_INFO_PO_NUMBER_CHANGED, EVENT_ORDER_INFO_TAG_FIELD2_CHANGED, EVENT_ORDER_INFO_TAG_FIELD_CHANGED, EVENT_ORDER_PAYMENT_LIST_CHANGED } from '@app/shared/app-events';
import { GaEventService, IGAEvent } from '@app/shared/services/ga-event.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { GaDeferredService, IGADeferred } from '@app/shared/services/ga-deferred.service';
import { OrderEntryStepId } from '../order-entry-steps';
import { CatalogStateService } from '../../catalog/catalog-state.service';
import { util } from '@app/shared/util';

@Component({
  selector: 'order-entry-info',
  template: `
    <h2 translate>ORDERENTRY.ORDERINFO.TITLE</h2>
    <formly [form]="form" [fields]="fields" [model]="model" [noValidate]="true" class="form-horizontal"></formly>
  `
})
export class OrderEntryInfoComponent implements IValidatingComponent, OnInit, AfterViewInit, OnDestroy {
  @Input() public stockOrderType:string;

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public model:IOrderInfo;
  private poNumberField:IExtraFieldConfigurationObject;
  private stockOrderTypeValueField:IExtraFieldConfigurationObject;
  private costCenterField:IExtraFieldConfigurationObject;
  private orderTypeField:IExtraFieldConfigurationObject;
  private wizard:GaWizard;
  private eventHandler:Subscription;
  private orderTypesPromise:Promise<any>;
  private costCentersPromise:Promise<any>;
  private stockOrderTypeValuePromise:Promise<any>;
  private onValidationReadyDeferred:IGADeferred<void>;
  private allowMasterStockOrders:boolean;

  constructor(
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private validateService:GaValidateService,
    private orderEntryInfoDataService:OrderEntryInfoDataService,
    private orderEntryInfoService:OrderEntryInfoService,
    private orderEntryWebshopService:OrderEntryWebshopService,
    private wizardService:GaWizardService,
    private orderEntryPaymentService:OrderEntryPaymentService,
    private eventService:GaEventService,
    private translationService:TranslationService,
    private catalogStateService:CatalogStateService,
    deferredService:GaDeferredService
  ) {
    this.onValidationReadyDeferred = deferredService.defer();
  }

  public ngOnInit():void {
    this.activate();
    this.listen();
  }

  public ngAfterViewInit():void {
    this.onValidationReadyDeferred.resolve();
  }

  private activate():void {
    this.initWizardSetup();

    this.model = this.orderEntryInfoService.orderInfo;

    if (this.orderEntryWebshopService.shopContext?.buyingGuide?.orderType) {
      this.model.orderType = {
        type: this.orderEntryWebshopService.shopContext.buyingGuide.orderType,
        id: undefined
      };
    }

    this.allowMasterStockOrders = this.catalogStateService.allowMasterStockOrders;
    this.fields = this.createFields();
    this.setTagFieldContext();
    this.updatePONumberRequired();
    this.fillFields();
  }

  private initWizardSetup():void {
    this.wizard = this.wizardService.getInstance(WIZARD_ORDER);

    // register current validator to use validateCurrent() when saving
    this.wizard.currentValidator(this.validate, true, this.beforeValidate, OrderEntryStepId.ORDERINFO);
  }

  private fillFields():void {
    this.orderTypesPromise =
      this.formlyService.fillField(this.orderTypeField, this.orderEntryInfoDataService.orderTypes(this.orderEntryWebshopService.selectedShop.id))
        .then((orderTypes:IOrderType[]) => {
          if(orderTypes.length){
            if (this.model.orderType) {
              // try to find the orderType in the list (this is useful for buying guide where only the orderType.type is specified
              const orderTypeFromList = _.find(orderTypes, (orderType:IOrderType) => {
                return orderType.id === this.model.orderType.id || orderType.type === this.model.orderType.type;
              });

              // if not found (can happen when orderType becomes inactive) -> use the current value anyway
              this.orderTypeField.formControl.patchValue(orderTypeFromList || this.model.orderType);
            }
          }
        });

    this.costCentersPromise =
      this.formlyService.fillField(this.costCenterField, this.orderEntryInfoDataService.costCenters(this.orderEntryWebshopService.selectedShop.id, this.orderEntryWebshopService.selectedCountry.id));

    if (this.allowMasterStockOrders) {
      // Retrieve the description value from the server based on the 'stockOrderType' URL parameter
      this.stockOrderTypeValuePromise = this.orderEntryInfoDataService.stockOrderTypes(this.orderEntryWebshopService.selectedShop.id, this.orderEntryWebshopService.selectedCountry.id, this.orderEntryWebshopService.shopContext.bp.location.companyCountry.id)
        .then((stockOrderTypes:IStockOrderType[]) => {
          const stockOptionType:IValueObject = _.find(stockOrderTypes, { id: this.stockOrderType });
          this.stockOrderTypeValueField.formControl.setValue(stockOptionType || this.model.stockOrderType || stockOrderTypes[ 0 ]);
          this.stockOrderTypeValueField.hide = util.isNullOrUndefinedOrEmpty(stockOptionType);
          return stockOrderTypes;
        });
    }
  }

  private listen():void {
    this.form.valueChanges.subscribe(() => {
      // we can't use the model that is coming from the event, because the form does not always contain all fields
      // for fields that are hidden, the model value will be undefined
      this.orderEntryInfoService.orderInfo = _.clone(this.model);
    });

    this.eventHandler = this.eventService.events.subscribe((event:IGAEvent) => {
      switch(event.name){
        case EVENT_ORDER_INFO_TAG_FIELD_CHANGED:
          this.setTagFieldContext(event.data);
          break;

        case EVENT_ORDER_INFO_TAG_FIELD2_CHANGED:
          this.setTagFieldContext(undefined, event.data);
          break;

        case EVENT_ORDER_CONTEXT_UPDATED:
        case EVENT_ORDER_PAYMENT_LIST_CHANGED:
          this.updatePONumberRequired();
          break;

        case EVENT_ORDER_INFO_PO_NUMBER_CHANGED:
          this.model.customerPONumber = event.data;
          break;
      }
    });
  }

  private updatePONumberRequired():void {
    if(this.poNumberField) {
      const shopContext:IOrderContext = this.orderEntryWebshopService.shopContext;
      const allowSubmit:boolean = shopContext.orderPermissions && shopContext?.orderPermissions?.allowSubmit;
      const orderApprovalPOMandatory:boolean = shopContext?.orderApprovalPOMandatory;
      const paymentTypes:IDescribedStringObject[] = this.orderEntryPaymentService.paymentTypes;

      if (allowSubmit === false && !orderApprovalPOMandatory) {
        this.poNumberField.templateOptions.required = false;
      } else {
        this.poNumberField.templateOptions.required = shopContext?.poType === POType.MANDATORY || (paymentTypes?.length === 1 && paymentTypes[ 0 ].id === 'PO');
      }
    }
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const fields:IExtraFieldConfigurationObject[] = [];

    fields.push(
      this.formlyService.aValue('orderCreationDate', 'ORDERENTRY.ORDERINFO.CREATION_DATE')
        .withFormat(this.formattingService.formatDate)
        .build(),

      this.formlyService.anInput('description', 'ORDERENTRY.ORDERINFO.DESCRIPTION')
        .withMaxLength(100)
        .withPlaceholder('ORDERENTRY.ORDERINFO.DESCRIPTION_PH')
        .build(),

      this.formlyService.anInput('referenceCustomer', 'ORDERENTRY.ORDERINFO.REF_CUST')
        .withMaxLength(255)
        .withPlaceholder('ORDERENTRY.ORDERINFO.REF_CUST_PH')
        .build()
    );

    // if there are multiple payment types, the payment tab will take over validation
    if ([POType.MANUAL, POType.MANDATORY].indexOf(this.orderEntryWebshopService.shopContext.poType) > -1) {
      fields.push(
        this.poNumberField = this.formlyService.anInput('customerPONumber', 'ORDERENTRY.ORDERINFO.PONR')
          .isNotRequired()
          .withMaxLength(40)
          .withPlaceholder('ORDERENTRY.ORDERINFO.PONR_PH')
          .build()
      );
    }

    fields.push(
      this.formlyService.aDateInput('arrivalDate', 'ORDERENTRY.ORDERINFO.ARR_DATE')
        .build(),

      this.formlyService.aTextarea('comment', 'GA.GENERAL.COMMENTS')
        .withMaxLength(1000)
        .withPlaceholder('ORDERENTRY.ORDERINFO.COMMENTS_PH')
        .build(),

      this.orderTypeField = this.formlyService.aSelect('orderType', 'ORDERENTRY.ORDERINFO.ORDER_TYPE')
        .isRequired()
        .withFormat(this.formattingService.formatLov)
        .withHideExpression(() => !this.orderTypeField.templateOptions.options.length)
        .onChange((orderType:IOrderType) => {
          this.setApprovalNeeded(orderType);
        })
        .build(),

      this.costCenterField = this.formlyService.aSelect('costCenter', 'ORDERENTRY.ORDERINFO.COST_CENTER')
        .withRequired(this.orderEntryWebshopService.shopContext.costCenterRequired)
        .withFormat(this.formattingService.formatLov)
        .withHideExpression(() => !this.costCenterField.templateOptions.options.length)
        .build()
    );

    if (this.allowMasterStockOrders) {
      fields.push(
        this.stockOrderTypeValueField = this.formlyService.aValue('stockOrderType', 'ORDERENTRY.ORDERINFO.STOCK_ORDER_TYPE')
        .isHide()
        .withFormat(this.formattingService.formatLov)
        .build()
      );
    }

    return fields;
  }

  private setApprovalNeeded(orderType:IOrderType):void {
    this.orderEntryWebshopService.shopContext.approvalNeeded = orderType?.approvalNeeded;
  }

  public beforeValidate = ():Promise<void> => {
    const allPromises:Promise<any>[] = [
      this.orderTypesPromise,
      this.costCentersPromise,
      this.stockOrderTypeValuePromise
    ];

    if (this.allowMasterStockOrders) {
      allPromises.push(this.stockOrderTypeValuePromise);
    }

    // make sure all promises are resolved before validating
    return this.onValidationReadyDeferred.promise
      .then(() => Promise.all(allPromises).then(util.noop));
  };

  public validate = ():Promise<void|string[]> => {
    return this.validateService.validateFields(this.fields, this.model, true);
  };

  private setTagFieldContext(tagFieldContext?:IOrderContextTag, tagField2Context?:IOrderContextTag):void {
    tagFieldContext = this.orderEntryInfoService.tagField || tagFieldContext;
    tagField2Context = this.orderEntryInfoService.tagField2 || tagField2Context;

    const createTagField = (key:string, context:IOrderContextTag) => {
      if(context) {
        const tagField = this.formlyService.anInput(key, context.label)
          .withMaxLength(100)
          .showInfo()
          .withInfo(context.description)
          .withRequired(context.required)
          .withPlaceholder(this.translationService.instant('ORDERENTRY.ORDERINFO.TAG_PLACEHOLDER', { label: context.label }))
          .withPattern(context.regex)
          .build();

        const referenceCustomerIdx:number = _.findIndex(this.fields, { key: 'referenceCustomer' });
        this.fields.splice(referenceCustomerIdx + 1, 0, tagField);
      }
    };

    createTagField('tagValue2', tagField2Context);
    createTagField('tagValue', tagFieldContext);
  }

  public ngOnDestroy(): void {
    this.eventHandler?.unsubscribe();
    this.wizard.clearCurrentValidator();
  }
}